import axios, { AxiosError } from "axios";
import { debug as d } from "debug";
import {
  CreateRequestNotificationApiModel,
  NotificationApiModel,
  CreateTeamMemberRequestCompletedApiModel,
  PaginatedResponseApiModel,
  NotificationStatusApiModel,
} from "../api-models";
import useTeamLeadLevels, { LevelItem } from "./useTeamLeadLevels";
import cms from "../services/cms/cms";
import { csContext } from "../services/cs-context";
import { CmsDocumentTypes } from "../services/cms/cms-type-configuration";
import { CmsDocument } from "../services/cms/cms-type-definitions";

const debug = d("useNotification");

type ApiPostResponseType = {
  success: boolean;
  error: string | null;
};

export type SentRequestNotification = {
  resourceId: string | undefined;
  deadline: string | undefined;
};

export type CompletionNotification = {
  userId: string;
  teamId: string;
  prismicDoc: CmsDocument | null;
  linkResourceId?: string;
  linkResourceName?: string;
};

const { castAsLevel } = useTeamLeadLevels();

const getNotificationsForUser = async (
  userId: string,
  page: number,
  pageSize: number,
): Promise<PaginatedResponseApiModel<NotificationApiModel>> => {
  try {
    const response = await axios.get<PaginatedResponseApiModel<NotificationApiModel>>(
      `/api/usernotifications/${userId}?page=${page}&pageSize=${pageSize}`,
    );
    return response.data;
  } catch (error) {
    debug("Error getting notifications for user:", (error as AxiosError).response?.data.message);
    return {
      items: [],
      page: 0,
      pageSize: 0,
      totalCount: 0,
    };
  }
};

const createRequestNotifications = async (
  createNotificationModel: CreateRequestNotificationApiModel,
): Promise<ApiPostResponseType> => {
  try {
    const response = await axios.post("/api/usernotifications/request", createNotificationModel);
    return {
      success: response.status === 200,
      error: null,
    };
  } catch (error) {
    return {
      success: false,
      error: (error as AxiosError).response?.data.message ?? "An error occurred",
    };
  }
};

async function notifyTeamMembers(level: LevelItem, teamId: string | undefined, deadline: string) {
  if (!teamId) {
    return {
      success: false,
      error: "No team id provided",
    };
  }

  const castedLevel = castAsLevel(level.data);

  const isInitiatingPathway = castedLevel.initiate_pathway ?? false;

  const path = isInitiatingPathway
    ? cms.linkResolver(castAsLevel(level.data).pathway_to_initiate)
    : cms.linkResolver(castedLevel.resource_link);

  const notificationLink = `${window.location.origin}${path}`.replace("5000", "8080");

  const resourceId = isInitiatingPathway
    ? castedLevel.pathway_to_initiate?.id ?? ""
    : castedLevel.action_resource_link?.id ?? "";

  const resourceName = isInitiatingPathway
    ? cms.asText(castedLevel.pathway_to_initiate?.data?.title)
    : cms.asText(castedLevel.action_resource_link?.data.title);

  const resourceType = castedLevel.initiate_pathway ? "Pathway" : "Resource";

  const model: CreateRequestNotificationApiModel = {
    deadline,
    teamId,
    notificationLink,
    resourceId,
    resourceName, // need to get the name of the resource not the level
    notificationType: "TeamLeadResourceRequest",
    actorId: csContext.user.id,
    resourceType,
  };

  const response = await createRequestNotifications(model);

  return response;
}

async function createCompletionNotification(notificationData: CompletionNotification) {
  const { userId, teamId, prismicDoc, linkResourceId, linkResourceName } = notificationData;

  if (!prismicDoc && !linkResourceId) {
    return {
      success: false,
      error: "No document provided",
    };
  }

  if (prismicDoc) {
    const docType = prismicDoc.type;

    if (
      docType !== CmsDocumentTypes.CmsResource &&
      docType !== CmsDocumentTypes.CmsSession &&
      docType !== CmsDocumentTypes.CmsSessionWithSlides
    ) {
      return {
        success: false,
        error: "Invalid document type",
      };
    }
  }

  // we use the linkResourceId if the resource is hard-coded (eg, viewpoints pathways)
  const notificationModel: CreateTeamMemberRequestCompletedApiModel = {
    teamId,
    actorId: userId,
    resourceName: prismicDoc ? cms.asText(prismicDoc.data.title) : linkResourceName ?? "",
    resourceId: prismicDoc?.id ?? linkResourceId ?? "",
    notifierId: "",
    notificationType: "TeamMemberResourceCompleted",
    resourceType: "Resource",
  };

  try {
    const response = await axios.post("/api/usernotifications/complete", notificationModel);
    return {
      success: response.status === 200,
      error: null,
    };
  } catch (error) {
    return {
      success: false,
      error: (error as AxiosError).response?.data.message ?? "An error occurred",
    };
  }
}

async function getSentRequestNotificationsForTeam(
  teamId?: string,
): Promise<SentRequestNotification[]> {
  if (!teamId) {
    return [];
  }

  try {
    const response = await axios.get<NotificationApiModel[]>(
      `/api/usernotifications/request/team/${teamId}`,
    );
    return (
      response.data.map((notification) => {
        return {
          resourceId: notification.resourceId,
          deadline: notification.deadline,
        };
      }) ?? []
    );
  } catch (error) {
    debug("Error getting sent request notifications for team:", error);
    return [];
  }
}

async function updateStatus(
  id: string,
  newStatus: NotificationStatusApiModel,
): Promise<ApiPostResponseType> {
  try {
    const response = await axios.patch(`/api/usernotifications/${id}/${newStatus}`);
    return {
      success: response.status === 200,
      error: null,
    };
  } catch (error) {
    debug("Error updating notification status:", error);
    return {
      success: false,
      error: `Error updating notification status: ${(error as AxiosError).response?.data.message}`,
    };
  }
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export default function useNotification() {
  return {
    getNotificationsForUser,
    notifyTeamMembers,
    createCompletionNotification,
    getSentRequestNotificationsForTeam,
    updateStatus,
  };
}
